import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export interface ProductSlugTranslation {
  template_slug: string;
  language: string;
  localized_silo: string;
  localized_slug: string;
}

/**
 * Fetch all product slug translations
 */
export function useProductSlugTranslationsData() {
  return useQuery({
    queryKey: ['product-slug-translations'],
    queryFn: async (): Promise<ProductSlugTranslation[]> => {
      const { data, error } = await supabase
        .from('product_slug_translations' as any)
        .select('template_slug, language, localized_silo, localized_slug');

      if (error) {
        console.error('Error fetching product slug translations:', error);
        return [];
      }

      return (data as any) || [];
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60 * 24,
  });
}

/**
 * Get the localized product URL for a given template slug and language
 */
export function getLocalizedProductUrl(
  translations: ProductSlugTranslation[],
  templateSlug: string,
  language: string
): string {
  const t = translations.find(
    (tr) => tr.template_slug === templateSlug && tr.language === language
  );
  
  if (t) {
    if (language === 'en') {
      return `${t.localized_silo}/${t.localized_slug}`;
    }
    return `/${language}${t.localized_silo}/${t.localized_slug}`;
  }
  
  // Fallback to English
  const enT = translations.find(
    (tr) => tr.template_slug === templateSlug && tr.language === 'en'
  );
  if (enT) {
    if (language === 'en') {
      return `${enT.localized_silo}/${enT.localized_slug}`;
    }
    return `/${language}${enT.localized_silo}/${enT.localized_slug}`;
  }
  
  // Ultimate fallback
  const prefix = language === 'en' ? '' : `/${language}`;
  return `${prefix}/shop/id-card-generator/${templateSlug}`;
}

/**
 * Resolve a localized slug back to a template slug
 */
export function resolveTemplateSlug(
  translations: ProductSlugTranslation[],
  localizedSlug: string,
  language?: string
): string | null {
  // Try exact match on language first
  if (language) {
    const t = translations.find(
      (tr) => tr.localized_slug === localizedSlug && tr.language === language
    );
    if (t) return t.template_slug;
  }
  
  // Try any language
  const t = translations.find((tr) => tr.localized_slug === localizedSlug);
  if (t) return t.template_slug;
  
  // Maybe it's already a template slug
  const direct = translations.find((tr) => tr.template_slug === localizedSlug);
  if (direct) return direct.template_slug;
  
  return null;
}

/**
 * Hook providing localized product URL helpers
 */
export function useLocalizedProductUrls() {
  const { language } = useLanguage();
  const { data: translations = [] } = useProductSlugTranslationsData();

  const getProductUrl = (templateSlug: string): string => {
    return getLocalizedProductUrl(translations, templateSlug, language);
  };

  const resolveSlug = (localizedSlug: string): string | null => {
    return resolveTemplateSlug(translations, localizedSlug, language);
  };

  return { getProductUrl, resolveSlug, translations };
}
